export const ADMIN_PROSPECT_CAMPAIGNS_PAGE_SIZE = 25;
export const ADMIN_PROSPECT_CAMPAIGN_TARGETS_PAGE_SIZE = 25;
export const ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE = 25;
export const ADMIN_PROSPECT_CAMPAIGN_NAME_MAX = 120;

function clampPageSize(pageSize, fallback) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : fallback;
}

/**
 * @param {{
 *   name: string,
 *   selection: Record<string, unknown>,
 *   operationId: string,
 *   letterTemplate?: { templateId: string, version: number } | null,
 * }} params
 */
export function buildCreateAdminProspectCampaignRequest({
  name,
  selection,
  operationId,
  letterTemplate,
}) {
  const request = {
    name: String(name ?? '').trim(),
    selection,
    operationId: String(operationId ?? '').trim(),
  };
  if (letterTemplate && typeof letterTemplate === 'object') {
    request.letterTemplate = {
      templateId: String(letterTemplate.templateId ?? '').trim(),
      version: Number(letterTemplate.version),
    };
  }
  return request;
}

/**
 * Generate a client-side create operationId once per logical attempt.
 */
export function generateAdminProspectCampaignOperationId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `campaign-op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * Mutable create-attempt session. Keeps one operationId + exact payload across
 * ambiguous/transient retries for the same open dialog. Reset on open/close.
 *
 * @returns {{
 *   reset: () => void,
 *   hasAttempt: () => boolean,
 *   getAttemptPayload: () => ReturnType<typeof buildCreateAdminProspectCampaignRequest> | null,
 *   resolveAttemptPayload: (params: {
 *     name: string,
 *     selection: Record<string, unknown>,
 *     letterTemplate?: { templateId: string, version: number } | null,
 *     generateOperationId?: () => string,
 *   }) => ReturnType<typeof buildCreateAdminProspectCampaignRequest>,
 * }}
 */
export function createAdminProspectCampaignCreateAttempt() {
  let operationId = null;
  let attemptPayload = null;

  return {
    reset() {
      operationId = null;
      attemptPayload = null;
    },
    hasAttempt() {
      return attemptPayload != null;
    },
    getAttemptPayload() {
      return attemptPayload;
    },
    resolveAttemptPayload({
      name,
      selection,
      letterTemplate = null,
      generateOperationId = generateAdminProspectCampaignOperationId,
    }) {
      if (attemptPayload) {
        return attemptPayload;
      }
      operationId = generateOperationId();
      attemptPayload = buildCreateAdminProspectCampaignRequest({
        name,
        selection,
        operationId,
        letterTemplate,
      });
      return attemptPayload;
    },
  };
}

/**
 * @param {{ pageSize?: number, pageToken?: string | null }} [params]
 */
export function buildListAdminProspectCampaignsRequest({
  pageSize = ADMIN_PROSPECT_CAMPAIGNS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    pageSize: clampPageSize(pageSize, ADMIN_PROSPECT_CAMPAIGNS_PAGE_SIZE),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * @param {{ campaignId: string }} params
 */
export function buildGetAdminProspectCampaignRequest({ campaignId }) {
  return { campaignId: String(campaignId ?? '').trim() };
}

/**
 * @param {{
 *   campaignId: string,
 *   pageSize?: number,
 *   pageToken?: string | null,
 * }} params
 */
export function buildListAdminProspectCampaignTargetsRequest({
  campaignId,
  pageSize = ADMIN_PROSPECT_CAMPAIGN_TARGETS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    campaignId: String(campaignId ?? '').trim(),
    pageSize: clampPageSize(pageSize, ADMIN_PROSPECT_CAMPAIGN_TARGETS_PAGE_SIZE),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * @param {{ campaignId: string, operationId: string }} params
 */
export function buildPrepareAdminProspectCampaignMailingsRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    operationId: String(operationId ?? '').trim(),
  };
}

/**
 * @param {{ campaignId: string, operationId: string }} params
 */
export function buildRetryAdminProspectCampaignBuildRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    operationId: String(operationId ?? '').trim(),
  };
}

export function buildRetryAdminProspectCampaignMailingBuildRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId || '').trim(),
    operationId: String(operationId || '').trim(),
  };
}

/**
 * Mutable operation-id session for prepare / retry. Keeps one operationId across
 * duplicate clicks and ambiguous retries for the same logical attempt.
 *
 * @returns {{
 *   reset: () => void,
 *   hasAttempt: () => boolean,
 *   getOperationId: () => string | null,
 *   resolveOperationId: (generateOperationId?: () => string) => string,
 * }}
 */
export function createAdminProspectCampaignOperationAttempt() {
  let operationId = null;

  return {
    reset() {
      operationId = null;
    },
    hasAttempt() {
      return operationId != null;
    },
    getOperationId() {
      return operationId;
    },
    resolveOperationId(generateOperationId = generateAdminProspectCampaignOperationId) {
      if (operationId) return operationId;
      operationId = generateOperationId();
      return operationId;
    },
  };
}

/**
 * @param {{ campaignId: string, operationId: string }} params
 */
export function buildGenerateAdminProspectCampaignLettersRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    operationId: String(operationId ?? '').trim(),
  };
}

/**
 * @param {{
 *   campaignId: string,
 *   pageSize?: number,
 *   pageToken?: string | null,
 * }} params
 */
export function buildListAdminProspectCampaignMailingsRequest({
  campaignId,
  pageSize = ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    campaignId: String(campaignId ?? '').trim(),
    pageSize: clampPageSize(pageSize, ADMIN_PROSPECT_CAMPAIGN_MAILINGS_PAGE_SIZE),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * @param {{ campaignId: string, mailingId: string }} params
 */
export function buildGetAdminProspectCampaignLetterDownloadUrlRequest({ campaignId, mailingId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    mailingId: String(mailingId ?? '').trim(),
  };
}

/**
 * @param {{ campaignId: string, operationId: string }} params
 */
export function buildRequestAdminProspectCampaignLetterArchiveRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    operationId: String(operationId ?? '').trim(),
  };
}

/**
 * @param {{ campaignId: string, operationId: string }} params
 */
export function buildRetryAdminProspectCampaignLetterArchiveRequest({ campaignId, operationId }) {
  return {
    campaignId: String(campaignId ?? '').trim(),
    operationId: String(operationId ?? '').trim(),
  };
}

/**
 * @param {{ campaignId: string }} params
 */
export function buildGetAdminProspectCampaignLetterArchiveDownloadUrlRequest({ campaignId }) {
  return { campaignId: String(campaignId ?? '').trim() };
}
